import { getAvailabilitiesByCapacity } from '../../mapper/mapToAvailabilities';
import { type Availability } from '../../types/availability';

type CalendarDay = {
    date: Date;
    availabilities: Availability[];
    isAvailable: boolean;
    amount: number;
};

type AvailabilityCalendarViewModel = {
    days: CalendarDay[];
    endMonth: Date;
    startMonth: Date;
    visibleMonth: Date;
};

const getStartOfToday = (): Date => {
    const today = new Date();

    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

const getMonthDate = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const getDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const getDaysOfMonth = (visibleMonth: Date): Date[] => {
    const year = visibleMonth.getFullYear();
    const month = visibleMonth.getMonth();
    const numberOfDays = new Date(year, month + 1, 0).getDate();

    return Array.from({ length: numberOfDays }, (_, index) => new Date(year, month, index + 1));
};

const getInitialVisibleMonth = (availabilities: Availability[]): Date =>
    getMonthDate(availabilities[0]?.startDate ?? getStartOfToday());

const buildAvailabilityCalendarViewModel = (
    visibleMonth: Date,
    availabilities: Availability[],
    ticketQuantity: number = 1,
): AvailabilityCalendarViewModel => {
    const filteredAvailabilities = getAvailabilitiesByCapacity(
        availabilities,
        ticketQuantity,
    ).filter(availability => availability.startDate >= getStartOfToday());
    const normalizedVisibleMonth = getMonthDate(visibleMonth);
    const availabilitiesForMonth = filteredAvailabilities.filter(
        availability =>
            availability.startDate.getMonth() === normalizedVisibleMonth.getMonth() &&
            availability.startDate.getFullYear() === normalizedVisibleMonth.getFullYear(),
    );
    const availabilitiesByDay = new Map<string, Availability[]>();

    for (const availability of availabilitiesForMonth) {
        const key = getDateKey(availability.startDate);
        const currentDayAvailabilities = availabilitiesByDay.get(key) ?? [];

        currentDayAvailabilities.push(availability);
        availabilitiesByDay.set(key, currentDayAvailabilities);
    }

    const days = getDaysOfMonth(normalizedVisibleMonth).map(date => {
        const dayAvailabilities = availabilitiesByDay.get(getDateKey(date)) ?? [];

        return {
            date,
            availabilities: dayAvailabilities,
            isAvailable: dayAvailabilities.length > 0,
            amount: dayAvailabilities.length,
        };
    });

    return {
        days,
        endMonth: getMonthDate(
            filteredAvailabilities[filteredAvailabilities.length - 1]?.startDate ??
                normalizedVisibleMonth,
        ),
        startMonth: getMonthDate(filteredAvailabilities[0]?.startDate ?? normalizedVisibleMonth),
        visibleMonth: normalizedVisibleMonth,
    };
};

export { buildAvailabilityCalendarViewModel, type CalendarDay, getDateKey, getInitialVisibleMonth };
